import type { Event } from '@sentry/browser'

import {
  ApiAdapter,
  createServicesProvider,
  initializer,
  StorageAdapter,
} from '@tino-frontend/adapters'

import {
  REACT_APP_AUTH_API_URL,
  REACT_APP_CHECKOUT_BFF_API_URL,
  REACT_APP_CORE_API_URL,
  REACT_APP_ENV,
  REACT_APP_FIREBASE_API_KEY,
  REACT_APP_FIREBASE_APP_ID,
  REACT_APP_FIREBASE_AUTH_API_KEY,
  REACT_APP_FIREBASE_AUTH_DOMAIN,
  REACT_APP_FIREBASE_MEASUREMENT_ID,
  REACT_APP_FIREBASE_PDF_STORAGE_BUCKET,
  REACT_APP_FIREBASE_PROJECT_ID,
  REACT_APP_LADISLAS_API_URL,
  REACT_APP_MERCHANT_BFF_API_URL,
  REACT_APP_NAME,
  REACT_APP_SENTRY,
  REACT_APP_SINGLE_PLAYER_API_URL,
  REACT_APP_SITE_API_URL,
  REACT_APP_SUPPLIER_BFF_API_URL,
  REACT_APP_VERSION,
} from 'constants/envs'

import { AddressService } from './address.service'
import { AuthService } from './auth.service'
import { BillService } from './bill.service'
import { CheckoutService } from './checkout.service'
import {
  ClientListService,
  ClientProfileService,
  ClientKycService,
  ClientOrderService,
} from './client'
import { analyticsSetInterceptor } from './compose'
import { ContentService } from './content.service'
import { ContractService } from './contract.service'
import { CreditLimitService } from './credit-limit.service'
import { CSATService } from './csat.service'
import { EventsService } from './events.service'
import { FeatureFlagService } from './feature-flag.service'
import { InvoicesService } from './invoices.service'
import { LegalPersonService } from './legal-person.service'
import { LimitReservationsService } from './limit-reservations.service'
import { MerchantService } from './merchant.service'
import { NotificationCentralService } from './notification-central.service'
import { NotificationService } from './notification.service'
import { OwnerService } from './owner.service'
import { PaymentLinkService } from './payment-link.service'
import { PaymentService } from './payment.service'
import { PolicyService } from './policy.service'
import { PowerOfAttorneyService } from './power-of-attorney.service'
import { QuizService } from './quiz.service'
import { ReceivableService } from './receivable.service'
import { SalespersonService } from './salesperson.service'
import { SignInService } from './sign-in.service'
import { SignUpService } from './sign-up.service'
import { SinglePlayerService } from './single-player.service'
import { SupplierService } from './supplier.service'
import { TutorialService } from './tutorial.service'
import { UserManagementService } from './user-management.service'
import { UserService } from './user.service'
import { ValidationsService } from './validations.service'
import { WebViewService } from './web-view.service'
import { MerchantTourService } from './merchant-tour.service'
import { SentryConfig } from './sentry-config.service'
import { PartnerService } from './partner.service'
import { PaymentObligationsService } from './payment-obligations.service'
import { TermsService } from './terms.service'

const siteAdapter = new ApiAdapter(REACT_APP_SITE_API_URL)
const storageAdapter = new StorageAdapter('localStorage')
const apiAdapter = new ApiAdapter(REACT_APP_CORE_API_URL)
const ladislasAdapter = new ApiAdapter(REACT_APP_LADISLAS_API_URL)
const merchantBFFAdapter = new ApiAdapter(REACT_APP_MERCHANT_BFF_API_URL)
const supplierBFFAdapter = new ApiAdapter(REACT_APP_SUPPLIER_BFF_API_URL)
const checkoutAdapter = new ApiAdapter(REACT_APP_CHECKOUT_BFF_API_URL)
const authAPIAdapter = new ApiAdapter(REACT_APP_AUTH_API_URL)
const singlePlayerAdapter = new ApiAdapter(REACT_APP_SINGLE_PLAYER_API_URL)

const interceptorsAuthJWTToken = [
  apiAdapter,
  ladislasAdapter,
  authAPIAdapter,
  merchantBFFAdapter,
  supplierBFFAdapter,
  checkoutAdapter,
]

const sentry = new SentryConfig()

const { authAdapter, Sentry } = initializer({
  appConfig: {
    apiKey: REACT_APP_FIREBASE_API_KEY,
    projectId: REACT_APP_FIREBASE_PROJECT_ID,
    appId: REACT_APP_FIREBASE_APP_ID,
    measurementId: REACT_APP_FIREBASE_MEASUREMENT_ID,
  },
  authConfig: {
    apiKey: REACT_APP_FIREBASE_AUTH_API_KEY,
    authDomain: REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: REACT_APP_FIREBASE_PDF_STORAGE_BUCKET,
  },
  environment: REACT_APP_ENV as any,
  sentryConfig: {
    ignoreErrors: ['ChunkLoadError', 'Loading chunk'],
    release: `${REACT_APP_NAME}@${REACT_APP_VERSION}`,
    url: REACT_APP_SENTRY,
    beforeSend: (event: Event) => {
      return event
    },
    beforeBreadcrumb: sentry.beforeBreadcrumb,
    ...sentry.sessionReplay(),
  },
})

analyticsSetInterceptor([...interceptorsAuthJWTToken, siteAdapter])

type ServiceContextData = {
  auth: AuthService
  user: UserService
  invoices: InvoicesService
  policy: PolicyService
  legalPerson: LegalPersonService
  powerOfAttorney: PowerOfAttorneyService
  events: EventsService
  featureFlag: FeatureFlagService
  signIn: SignInService
  signUp: SignUpService
  userManagement: UserManagementService
  notification: NotificationService
  notificationCentral: NotificationCentralService
  bill: BillService
  tutorial: TutorialService
  content: ContentService
  clientList: ClientListService
  clientProfile: ClientProfileService
  clientKyc: ClientKycService
  clientOrder: ClientOrderService
  quiz: QuizService
  checkout: CheckoutService
  contract: ContractService
  receivable: ReceivableService
  supplier: SupplierService
  csat: CSATService
  creditLimit: CreditLimitService
  paymentLink: PaymentLinkService
  limitReservations: LimitReservationsService
  webView: WebViewService
  payment: PaymentService
  address: AddressService
  merchant: MerchantService
  singlePlayer: SinglePlayerService
  salesperson: SalespersonService
  validations: ValidationsService
  owner: OwnerService
  merchantTour: MerchantTourService
  partner: PartnerService
  paymentObligations: PaymentObligationsService
  terms: TermsService
}

function configService(): ServiceContextData {
  const signUp = new SignUpService(
    apiAdapter,
    merchantBFFAdapter,
    authAPIAdapter
  )
  const events = new EventsService(Sentry)
  const user = new UserService(apiAdapter, merchantBFFAdapter, storageAdapter)

  return {
    auth: new AuthService(
      authAdapter as any,
      interceptorsAuthJWTToken,
      signUp,
      authAPIAdapter,
      storageAdapter
    ),
    userManagement: new UserManagementService(checkoutAdapter),
    events,
    user,
    partner: new PartnerService(),
    invoices: new InvoicesService(merchantBFFAdapter, supplierBFFAdapter),
    policy: new PolicyService(apiAdapter),
    legalPerson: new LegalPersonService(apiAdapter, storageAdapter),
    powerOfAttorney: new PowerOfAttorneyService(apiAdapter, storageAdapter),
    featureFlag: new FeatureFlagService(apiAdapter),
    notification: new NotificationService(apiAdapter),
    notificationCentral: new NotificationCentralService(supplierBFFAdapter),
    tutorial: new TutorialService(merchantBFFAdapter),
    content: new ContentService(siteAdapter),
    bill: new BillService(merchantBFFAdapter),
    signUp,
    signIn: new SignInService(authAPIAdapter),
    quiz: new QuizService(apiAdapter),
    checkout: new CheckoutService(
      checkoutAdapter,
      authAPIAdapter,
      storageAdapter
    ),
    contract: new ContractService(merchantBFFAdapter),
    receivable: new ReceivableService(merchantBFFAdapter),
    supplier: new SupplierService(apiAdapter, supplierBFFAdapter),
    clientList: new ClientListService(supplierBFFAdapter),
    clientProfile: new ClientProfileService(apiAdapter, supplierBFFAdapter),
    clientKyc: new ClientKycService(supplierBFFAdapter),
    clientOrder: new ClientOrderService(supplierBFFAdapter),
    csat: new CSATService(
      merchantBFFAdapter,
      supplierBFFAdapter,
      storageAdapter
    ),
    creditLimit: new CreditLimitService(merchantBFFAdapter, supplierBFFAdapter),
    paymentLink: new PaymentLinkService(supplierBFFAdapter),
    limitReservations: new LimitReservationsService(supplierBFFAdapter),
    webView: new WebViewService(events, storageAdapter),
    payment: new PaymentService(merchantBFFAdapter),
    address: new AddressService(supplierBFFAdapter),
    merchant: new MerchantService(supplierBFFAdapter),
    singlePlayer: new SinglePlayerService(
      singlePlayerAdapter,
      merchantBFFAdapter
    ),
    salesperson: new SalespersonService(supplierBFFAdapter),
    validations: new ValidationsService(supplierBFFAdapter),
    owner: new OwnerService(merchantBFFAdapter),
    merchantTour: new MerchantTourService(),
    paymentObligations: new PaymentObligationsService(merchantBFFAdapter),
    terms: new TermsService(supplierBFFAdapter),
  }
}

export const {
  useServices: useService,
  ServicesProvider
} = createServicesProvider(configService(), () => null)
